import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  MessageSquare, Plus, Send, User, X, Trash2, Archive,
  Circle, Search, Clock, Loader2
} from 'lucide-react';
import { ScrollableTabs, FilterPill } from '../components/ui/view-toggle';
import axios from 'axios';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL;
const WS_URL = API.replace('https://', 'wss://').replace('http://', 'ws://');

function timeAgo(d) { if (!d) return ''; const s = Math.floor((new Date() - new Date(d)) / 1000); if (s < 60) return 'just now'; if (s < 3600) return `${Math.floor(s / 60)}m`; if (s < 86400) return `${Math.floor(s / 3600)}h`; return `${Math.floor(s / 86400)}d`; }

function NewRoomDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ visitor_name: '', visitor_email: '', subject: '' });
  const [saving, setSaving] = useState(false);
  const handleCreate = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/api/chat/rooms`, form);
      toast.success('Chat room created');
      onCreated(data);
      onClose();
      setForm({ visitor_name: '', visitor_email: '', subject: '' });
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><MessageSquare className="h-5 w-5 text-teal-500" /> New Chat</DialogTitle><DialogDescription>Start a conversation</DialogDescription></DialogHeader>
        <div className="space-y-3 mt-2">
          <div><Label className="text-xs">Visitor Name</Label><Input value={form.visitor_name} onChange={e => setForm(p => ({...p, visitor_name: e.target.value}))} placeholder="John Smith" data-testid="chat-visitor-name" /></div>
          <div><Label className="text-xs">Email</Label><Input value={form.visitor_email} onChange={e => setForm(p => ({...p, visitor_email: e.target.value}))} placeholder="john@example.com" /></div>
          <div><Label className="text-xs">Subject</Label><Input value={form.subject} onChange={e => setForm(p => ({...p, subject: e.target.value}))} placeholder="Question about pricing..." data-testid="chat-subject" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={handleCreate} disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="create-room-btn">{saving ? 'Creating...' : 'Start Chat'}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ChatPanel({ room, onClose, onRoomUpdated }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);

  useEffect(() => {
    if (!room) return;
    setLoading(true);
    axios.get(`${API}/api/chat/rooms/${room.id}/messages`)
      .then(r => { setMessages(r.data); setLoading(false); })
      .catch(() => setLoading(false));

    const ws = new WebSocket(`${WS_URL}/api/chat/ws/${room.id}`);
    ws.onmessage = (event) => {
      try {
        const parsed = JSON.parse(event.data);
        if (parsed.type === 'message') {
          setMessages(prev => {
            if (prev.find(m => m.id === parsed.data.id)) return prev;
            return [...prev, parsed.data];
          });
        }
      } catch {}
    };
    wsRef.current = ws;
    return () => { ws.close(); };
  }, [room]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/api/chat/rooms/${room.id}/messages`, {
        content: input, sender_type: 'agent', sender_name: 'Agent',
      });
      setInput('');
    } catch { toast.error('Send failed'); }
    finally { setSending(false); }
  };

  const handleClose = async () => {
    try {
      await axios.put(`${API}/api/chat/rooms/${room.id}/status?status=closed`, {});
      toast.success('Chat closed');
      onRoomUpdated({ ...room, status: 'closed' });
    } catch { toast.error('Failed'); }
  };

  if (!room) return null;

  return (
    <div className="flex flex-col h-full border-l bg-white" data-testid="chat-panel">
      <div className="flex items-center gap-3 px-4 py-3 border-b flex-shrink-0">
        <Button variant="ghost" size="icon" className="h-8 w-8 sm:hidden" onClick={onClose}><X className="h-4 w-4" /></Button>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold text-gray-900 truncate">{room.name || 'Chat'}</h3>
          <div className="flex items-center gap-2 text-[10px] text-gray-400">
            {room.visitor_name && <span>{room.visitor_name}</span>}
            {room.subject && <span className="truncate">· {room.subject}</span>}
          </div>
        </div>
        <Badge className={room.status === 'active' ? 'bg-green-100 text-green-700 text-[9px]' : 'bg-gray-100 text-gray-600 text-[9px]'}>{room.status}</Badge>
        {room.status === 'active' && (
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={handleClose} data-testid="close-chat-btn">Close</Button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0">
        {loading ? <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 text-teal-500 animate-spin" /></div> : messages.length === 0 ? (
          <div className="text-center py-8"><MessageSquare className="h-8 w-8 text-gray-300 mx-auto mb-2" /><p className="text-xs text-gray-400">No messages yet. Start the conversation!</p></div>
        ) : messages.map(m => (
          <div key={m.id} className={`flex ${m.sender_type === 'agent' ? 'justify-end' : 'justify-start'}`} data-testid={`msg-${m.id}`}>
            <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 ${
              m.sender_type === 'agent' ? 'bg-teal-600 text-white rounded-br-md' : 'bg-gray-100 text-gray-900 rounded-bl-md'
            }`}>
              <p className="text-sm whitespace-pre-wrap">{m.content}</p>
              <p className={`text-[9px] mt-1 ${m.sender_type === 'agent' ? 'text-teal-200' : 'text-gray-400'}`}>{m.sender_name} · {timeAgo(m.created_at)}</p>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {room.status === 'active' && (
        <div className="flex items-center gap-2 px-4 py-3 border-t flex-shrink-0">
          <Input value={input} onChange={e => setInput(e.target.value)} placeholder="Type a message..." className="flex-1"
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }} data-testid="chat-input" />
          <Button onClick={handleSend} disabled={sending || !input.trim()} className="bg-teal-600 hover:bg-teal-700" data-testid="send-chat-btn"><Send className="h-4 w-4" /></Button>
        </div>
      )}
    </div>
  );
}

export default function LiveChatPage() {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [newRoomOpen, setNewRoomOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);

  const fetchRooms = useCallback(async () => {
    try {
      const params = {};
      if (filter !== 'all') params.status = filter;
      const { data } = await axios.get(`${API}/api/chat/rooms`, { params,  });
      setRooms(data);
    } catch { toast.error('Failed to load chats'); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { fetchRooms(); }, [fetchRooms]);

  const handleCreated = (room) => { setRooms(p => [room, ...p]); setSelectedRoom(room); };
  const handleRoomUpdated = (updated) => { setRooms(p => p.map(r => r.id === updated.id ? updated : r)); setSelectedRoom(updated); };
  const handleDelete = async (id, e) => {
    e?.stopPropagation();
    if (!window.confirm('Delete this chat?')) return;
    try { await axios.delete(`${API}/api/chat/rooms/${id}`); setRooms(p => p.filter(r => r.id !== id)); if (selectedRoom?.id === id) setSelectedRoom(null); toast.success('Deleted'); } catch { toast.error('Failed'); }
  };

  const filtered = rooms.filter(r => !search || [r.name, r.visitor_name, r.subject].filter(Boolean).some(v => v.toLowerCase().includes(search.toLowerCase())));
  const activeCount = rooms.filter(r => r.status === 'active').length;

  return (
    <div className="animate-fadeIn" data-testid="live-chat-page">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center"><MessageSquare className="h-5 w-5 text-teal-500" /></div>
          <div><h1 className="text-xl sm:text-2xl font-heading font-bold text-text-primary">Live Chat<PageInfoTooltip pageKey="live-chat" /></h1><p className="text-xs text-gray-400">{rooms.length} conversations · {activeCount} active</p></div>
        </div>
        <Button onClick={() => setNewRoomOpen(true)} className="bg-teal-600 hover:bg-teal-700 h-8 gap-1 text-xs" data-testid="new-chat-btn"><Plus className="h-3.5 w-3.5" /> New Chat</Button>
      </div>

      <div className="flex border rounded-xl overflow-hidden bg-white" style={{ height: 'calc(100vh - 180px)', minHeight: '400px' }}>
        <div className={`w-full sm:w-80 flex-shrink-0 border-r flex flex-col ${selectedRoom ? 'hidden sm:flex' : 'flex'}`}>
          <div className="p-3 border-b space-y-2 flex-shrink-0">
            <div className="relative"><Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" /><Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search chats..." className="pl-8 h-8 text-xs" data-testid="chat-search" /></div>
            <ScrollableTabs>
              {['all', 'active', 'closed'].map(v => (
                <FilterPill key={v} active={filter === v} onClick={() => setFilter(v)} testId={`chat-filter-${v}`}>
                  {v === 'all' ? 'All' : v.charAt(0).toUpperCase() + v.slice(1)}
                </FilterPill>
              ))}
            </ScrollableTabs>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading ? <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 text-teal-500 animate-spin" /></div> : filtered.length === 0 ? (
              <div className="text-center py-8 px-4"><MessageSquare className="h-8 w-8 text-gray-300 mx-auto mb-2" /><p className="text-xs text-gray-400">{search ? 'No matches' : 'No chats yet'}</p></div>
            ) : filtered.map(r => (
              <div key={r.id} className={`flex items-center gap-3 px-3 py-3 border-b cursor-pointer transition-colors hover:bg-gray-50 group ${selectedRoom?.id === r.id ? 'bg-teal-50 border-l-2 border-l-teal-500' : ''}`}
                onClick={() => setSelectedRoom(r)} data-testid={`room-${r.id}`}>
                <div className="relative flex-shrink-0">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center text-white text-xs font-bold">
                    {(r.visitor_name?.[0] || 'V').toUpperCase()}
                  </div>
                  {r.status === 'active' && <Circle className="absolute -bottom-0.5 -right-0.5 h-3 w-3 text-green-500 fill-green-500" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xs font-semibold text-gray-900 truncate">{r.visitor_name || r.name || 'Visitor'}</h3>
                    <span className="text-[9px] text-gray-400 flex-shrink-0">{timeAgo(r.updated_at)}</span>
                  </div>
                  <p className="text-[10px] text-gray-500 truncate">{r.last_message || r.subject || 'No messages'}</p>
                </div>
                {r.unread_count > 0 && <span className="w-5 h-5 rounded-full bg-teal-500 text-white text-[9px] flex items-center justify-center flex-shrink-0">{r.unread_count}</span>}
                <Button variant="ghost" size="icon" className="h-6 w-6 flex-shrink-0 opacity-0 group-hover:opacity-100" onClick={e => handleDelete(r.id, e)}><Trash2 className="h-3 w-3 text-red-400" /></Button>
              </div>
            ))}
          </div>
        </div>

        <div className={`flex-1 flex flex-col ${!selectedRoom ? 'hidden sm:flex' : 'flex'}`}>
          {selectedRoom ? (
            <ChatPanel room={selectedRoom} onClose={() => setSelectedRoom(null)} onRoomUpdated={handleRoomUpdated} />
          ) : (
            <div className="flex-1 flex items-center justify-center"><div className="text-center"><MessageSquare className="h-12 w-12 text-gray-200 mx-auto mb-3" /><p className="text-sm text-gray-400">Select a chat to view messages</p><p className="text-xs text-gray-300 mt-1">or start a new conversation</p></div></div>
          )}
        </div>
      </div>

      <NewRoomDialog open={newRoomOpen} onClose={() => setNewRoomOpen(false)} onCreated={handleCreated} />
    </div>
  );
}
